/* Shared preview / Shopify behavior. Pricing and discounts stay in Shopify forms. */
(() => {
  'use strict';
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const mobile = window.matchMedia?.('(max-width: 900px)');
  const scenes = new Map();
  let guide = null, cleanupSticky = () => {};
  const watchMedia = (query, callback) => {
    if (query?.addEventListener) {
      query.addEventListener('change', callback);
      return () => query.removeEventListener('change', callback);
    }
    query?.addListener?.(callback);
    return () => query?.removeListener?.(callback);
  };
  const stickyClass = show => {
    if (document.body.classList.contains('sticky-purchase-visible') !== show) document.body.classList.toggle('sticky-purchase-visible', show);
  };
  function focusDestination(destination) {
    if (!destination.hasAttribute('tabindex')) {
      destination.setAttribute('tabindex', '-1');
      destination.addEventListener('blur', () => destination.removeAttribute('tabindex'), { once: true });
    }
    destination.focus({ preventScroll: true });
  }

  function initGuide() {
    const panel = document.querySelector('[data-soil-guide-panel]');
    const closeButton = panel?.querySelector('[data-soil-guide-close]');
    if (guide && guide.panel !== panel) { guide.cleanup(); guide = null; }
    if (!panel || !closeButton || typeof panel.showModal !== 'function') return;
    if (!guide) {
      const events = new AbortController();
      const listen = (target, type, callback) => target.addEventListener(type, callback, { signal: events.signal });
      const bound = new WeakSet();
      let opener = null, restoreFocus = true;
      function close(restore = true) { restoreFocus = restore; if (panel.open) panel.close(); }
      listen(closeButton, 'click', () => close());
      listen(panel, 'cancel', event => { event.preventDefault(); close(); });
      listen(panel, 'close', () => {
        const otherDialog = document.querySelector('dialog[open]');
        if (!otherDialog) document.body.classList.remove('modal-open');
        if (!otherDialog && restoreFocus && opener?.isConnected) opener.focus({ preventScroll: true });
        opener = null;
      });
      listen(panel, 'click', event => {
        if (event.target !== panel) return;
        const rect = panel.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
      });
      panel.querySelectorAll('[data-soil-guide-navigate]').forEach(link => listen(link, 'click', () => {
        close(false);
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash) return;
        const destination = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (destination) focusDestination(destination);
      }));
      const shop = panel.querySelector('[data-soil-guide-shop]');
      if (shop) listen(shop, 'click', () => { close(false); document.querySelector('.hero-add')?.click(); });
      guide = {
        panel,
        bind(trigger) {
          if (bound.has(trigger)) return;
          bound.add(trigger); trigger.hidden = false;
          listen(trigger, 'click', () => {
            if (panel.open) return;
            opener = document.activeElement; restoreFocus = true;
            panel.showModal(); document.body.classList.add('modal-open'); closeButton.focus();
          });
        },
        cleanup() {
          close(false); events.abort(); opener = null;
          if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
        }
      };
    }
    document.querySelectorAll('[data-soil-guide-open]').forEach(trigger => guide.bind(trigger));
  }

  function initWormScene() {
    scenes.forEach((cleanup, scene) => { if (!scene.isConnected) { cleanup(); scenes.delete(scene); } });
    document.querySelectorAll('[data-worm-scene]').forEach(scene => {
      const replay = scene.querySelector('[data-worm-replay]');
      const note = scene.querySelector('[data-worm-motion-note]');
      const worm = scene.querySelector('.hero-worm');
      if (!replay || !worm || scenes.has(scene)) return;
      const events = new AbortController();
      let timer, observer;
      function stop() {
        clearTimeout(timer);
        if (scene.classList.contains('is-playing')) scene.classList.remove('is-playing');
        replay.disabled = false;
      }
      function preference() { if (reduced?.matches) stop(); replay.hidden = !!reduced?.matches; if (note) note.hidden = !reduced?.matches; }
      function play() {
        if (reduced?.matches || scene.classList.contains('is-playing') || !scene.isConnected) return;
        scene.classList.add('is-playing'); replay.disabled = true;
        timer = setTimeout(stop, 4700);
      }
      preference();
      const unwatch = watchMedia(reduced, preference);
      replay.addEventListener('click', play, { signal: events.signal });
      worm.addEventListener('animationend', stop, { signal: events.signal });
      worm.addEventListener('animationcancel', stop, { signal: events.signal });
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { play(); observer.disconnect(); }
        }, { threshold: 0.35 });
        observer.observe(scene);
      }
      scenes.set(scene, () => { stop(); observer?.disconnect(); unwatch(); events.abort(); });
    });
  }

  function initStickyPurchase() {
    cleanupSticky();
    const bar = document.querySelector('[data-sticky-purchase]');
    const main = document.querySelector('[data-main-buy]');
    const notice = document.querySelector('.preview-bar');
    const header = document.querySelector('.header');
    const root = document.documentElement;
    const events = new AbortController();
    const observers = [];
    let purchaseObserver, observedHeaderHeight = -1, pendingFrame;
    function offsets() {
      const noticeHeight = notice && !notice.hidden ? Math.ceil(notice.getBoundingClientRect().height) : 0;
      root.style.setProperty('--preview-offset', noticeHeight + 'px');
      root.style.setProperty('--sticky-buy-height', bar && !bar.hidden ? Math.ceil(bar.getBoundingClientRect().height) + 'px' : '0px');
    }
    const release = () => {
      events.abort(); purchaseObserver?.disconnect(); observers.forEach(observer => observer.disconnect());
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      if (bar) bar.hidden = true;
      stickyClass(false); offsets();
    };
    cleanupSticky = release;
    offsets();
    if (!bar || !main) return;
    const targets = [...document.querySelectorAll('[data-main-buy], #shop .button')];
    const visible = new Map(targets.map(target => [target, false]));
    let mainPassed = false;
    const headerHeight = () => Math.max(0, header?.getBoundingClientRect().height || 0);
    function update() {
      const stickyButton = bar.querySelector('button');
      const show = !!mobile?.matches && !main.disabled && !stickyButton?.disabled && mainPassed && ![...visible.values()].some(Boolean) && !document.querySelector('dialog[open]');
      bar.hidden = !show; stickyClass(show); offsets();
    }
    function measure() {
      const cutoff = headerHeight();
      targets.forEach(target => { const rect = target.getBoundingClientRect(); visible.set(target, rect.bottom > cutoff && rect.top < window.innerHeight && rect.height > 0 && rect.width > 0); });
      mainPassed = main.getBoundingClientRect().bottom <= cutoff;
      update();
    }
    function observePurchases() {
      const height = Math.ceil(headerHeight());
      if (purchaseObserver && observedHeaderHeight === height) return;
      purchaseObserver?.disconnect(); observedHeaderHeight = height;
      purchaseObserver = new IntersectionObserver(entries => {
        for (const entry of entries) { visible.set(entry.target, entry.isIntersecting); if (entry.target === main) mainPassed = entry.boundingClientRect.bottom <= headerHeight(); }
        update();
      }, { rootMargin: '-' + height + 'px 0px 0px 0px', threshold: 0 });
      targets.forEach(target => purchaseObserver.observe(target));
    }
    const refresh = () => { if ('IntersectionObserver' in window) observePurchases(); measure(); };
    const unwatchMobile = watchMedia(mobile, refresh);
    if ('IntersectionObserver' in window) observePurchases();
    else window.addEventListener('scroll', () => {
      if (pendingFrame) return;
      pendingFrame = requestAnimationFrame(() => { pendingFrame = null; measure(); });
    }, { passive: true, signal: events.signal });
    window.addEventListener('resize', refresh, { passive: true, signal: events.signal });
    if ('ResizeObserver' in window) {
      const sizing = new ResizeObserver(refresh); observers.push(sizing);
      [header, notice, bar].filter(Boolean).forEach(element => sizing.observe(element));
    }
    if ('MutationObserver' in window) {
      const bodyChanges = new MutationObserver(update); observers.push(bodyChanges);
      bodyChanges.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      const controlChanges = new MutationObserver(refresh); observers.push(controlChanges);
      controlChanges.observe(main, { attributes: true, attributeFilter: ['disabled'] });
      if (notice) controlChanges.observe(notice, { attributes: true, attributeFilter: ['hidden'] });
      const dialogChanges = new MutationObserver(update); observers.push(dialogChanges);
      document.querySelectorAll('dialog').forEach(dialog => dialogChanges.observe(dialog, { attributes: true, attributeFilter: ['open'] }));
    }
    const previewBuy = bar.querySelector('[data-sticky-buy]');
    previewBuy?.addEventListener('click', () => document.querySelector('.hero-add')?.click(), { signal: events.signal });
    cleanupSticky = () => { unwatchMobile(); release(); };
    measure();
  }

  function refresh() { initGuide(); initWormScene(); initStickyPurchase(); }
  refresh();
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:unload', event => {
    scenes.forEach((cleanup, scene) => { if (event.target.contains(scene)) { cleanup(); scenes.delete(scene); } });
    if (guide && event.target.contains(guide.panel)) { guide.cleanup(); guide = null; }
    cleanupSticky();
    queueMicrotask(() => { initStickyPurchase(); });
  });
})();
